import { useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./useAuth";
import { isStreakMilestone } from "@/lib/acornRules";
import { fireEvent } from "@/lib/events";
import { invalidateProfile } from "./queries/useProfile";

/**
 * Call onMemorySaved(memoryId, opts?) after any successful memory creation.
 * Handles streak increment (once per day) and fires Mori events.
 * Acorn awards are handled by acornSubscribers reacting to those events.
 */
export function useMemorySaved() {
  const { user } = useAuth();

  const onMemorySaved = useCallback(
    async (
      memoryId: string,
      opts?: { memoryType?: string; contentLibraryId?: string | null },
    ) => {
      if (!user) return;

      const today = new Date().toISOString().split("T")[0];

      // ── Streak update (once per calendar day) ──────────────────────────────
      const { data: profile } = await supabase
        .from("profiles")
        .select("current_streak, longest_streak, last_debrief_date")
        .eq("id", user.id)
        .single();

      const p = profile as {
        current_streak?: number;
        longest_streak?: number;
        last_debrief_date?: string;
      } | null;

      const previousStreak = p?.current_streak ?? 0;
      let currentStreak = previousStreak;

      if (p?.last_debrief_date !== today) {
        currentStreak = previousStreak + 1;
        await supabase
          .from("profiles")
          .update({
            current_streak: currentStreak,
            longest_streak: Math.max(currentStreak, p?.longest_streak ?? 0),
            last_debrief_date: today,
          })
          .eq("id", user.id);
        void invalidateProfile(user.id);
      }

      // ── Claim first-today bonus — server-authoritative, atomic ────────────
      const { data: claimedSlot } = await supabase.rpc('claim_memory_acorn_slot');
      const slot = (claimedSlot ?? 'none') as 'first' | 'subsequent' | 'none';

      // ── Fire events — acornSubscribers handles all award logic ─────────────

      fireEvent("streak.updated", {
        userId: user.id,
        currentStreak,
        longestStreak: Math.max(currentStreak, p?.longest_streak ?? 0),
        isMilestone: isStreakMilestone(currentStreak),
        previousStreak,
      });

      // TODO: streak.broken is unreachable from this hook — onMemorySaved
      // only ever increments or holds the streak, never zeroes it. The real
      // detection needs to live at home/login mount (last_debrief_date diff).
      // Guarded fire left here so the wiring exists; follow-up prompt moves
      // the detection to the right place.
      if (currentStreak === 0 && previousStreak > 0) {
        fireEvent("streak.broken", {
          userId: user.id,
          previousStreak,
        });
      }

      fireEvent("memory.created", {
        userId: user.id,
        memoryId,
        memoryType: opts?.memoryType ?? "voice",
        contentLibraryId: opts?.contentLibraryId ?? null,
        slot,
      });

      if (opts?.contentLibraryId) {
        fireEvent("challenge.completed", {
          userId: user.id,
          memoryId,
          contentLibraryId: opts.contentLibraryId,
          completionType: "memory",
        });
      }
    },
    [user],
  );

  return { onMemorySaved };
}
